/**
 * Discharged-Client Re-Engagement: client-side config mirror.
 *
 * Mirrors `services/client-functions/src/discharged-reengagement/types.ts`
 * (the source of truth). Kept as a local mirror rather than a shared package to
 * avoid a full-fleet redeploy on every iteration. Keep the two in sync.
 *
 * Used by the admin settings page (ReengagementSettingsPage) and the gated
 * Send UI on the Discharged Loans page.
 */

export type ReengagementFromAddressMode = 'broker' | 'noreply';

export type ReengagementConsentBasis =
  | 'existing_business_relationship'
  | 'express_consent'
  | 'manual_override';

export interface DischargedReengagementConfig {
  enabled: boolean;
  minAttempts: number;
  minWindowDays: number;
  requireDistinctDays: boolean;
  minDaysBetweenReengagementEmails: number;
  fromAddressMode: ReengagementFromAddressMode;
  maxSendsPerBrokerPerDay: number;
  crossSellBlurb: string | null;
  defaultConsentBasis: ReengagementConsentBasis;
  senderLegalName: string;
  senderAbn?: string | null;
  senderPostalAddress: string;
  senderContactPhone: string;
  senderContactEmail: string;
  templates?: ReengagementTemplatesOverride;
  emailAccentColor?: string | null;
  emailTagline?: string | null;
  emailServices?: ReengagementServiceItem[];
  emailTrustStats?: ReengagementTrustStat[];
  emailLocations?: string | null;
}

export interface ReengagementServiceItem {
  title: string;
  description: string;
}

export interface ReengagementTrustStat {
  value: string;
  label: string;
}

export type ReengagementVariant = 'voicemail_followup' | 'after_conversation';

export interface ReengagementTemplateOverride {
  subject?: string;
  heading?: string;
  intro?: string;
  body?: string;
  ctaLabel?: string;
  signOff?: string;
}

export interface ReengagementTemplatesOverride {
  voicemail_followup?: ReengagementTemplateOverride;
  after_conversation?: ReengagementTemplateOverride;
}

/** Default copy shown as placeholders in the editor (mirrors shared-emails). */
export const REENGAGEMENT_DEFAULT_COPY: Record<
  ReengagementVariant,
  Required<ReengagementTemplateOverride>
> = {
  voicemail_followup: {
    subject: 'We tried to reach you, {firstName}',
    heading: 'Sorry we missed you',
    intro:
      'We tried to give you a call today. A little while ago your loan moved on from us, and we wanted to check in. Not to sell you anything, just to understand how you are going and whether there was anything we could have done better.',
    body: 'Your feedback genuinely helps us look after every client. When you have a spare moment, we would love to hear from you, just reply to this email.',
    ctaLabel: 'Call us back',
    signOff: 'Talk soon,\n{callerName} and the {companyName} team',
  },
  after_conversation: {
    subject: 'Thanks for the chat, {firstName}',
    heading: 'Thanks for the chat',
    intro:
      'Thanks so much for taking my call today. It was great to hear how you are going and to understand a little more about your recent move. As promised, here is a quick overview of everything we can help with.',
    body: 'Whether it is for you, your family or a mate down the track, we would love to be the team behind your next move.',
    ctaLabel: 'Get in touch',
    signOff: 'Cheers,\n{callerName} and the {companyName} team',
  },
};

export const REENGAGEMENT_VARIANT_LABELS: Record<ReengagementVariant, string> = {
  voicemail_followup: 'Voicemail follow-up',
  after_conversation: 'After a conversation',
};

export const REENGAGEMENT_RULE_DEFAULTS: Omit<
  DischargedReengagementConfig,
  | 'enabled'
  | 'senderLegalName'
  | 'senderAbn'
  | 'senderPostalAddress'
  | 'senderContactPhone'
  | 'senderContactEmail'
> = {
  minAttempts: 4,
  minWindowDays: 21,
  requireDistinctDays: true,
  minDaysBetweenReengagementEmails: 90,
  fromAddressMode: 'broker',
  maxSendsPerBrokerPerDay: 25,
  crossSellBlurb: null,
  defaultConsentBasis: 'existing_business_relationship',
};

export const REENGAGEMENT_CONSENT_BASIS_LABELS: Record<ReengagementConsentBasis, string> = {
  existing_business_relationship: 'Existing business relationship',
  express_consent: 'Express consent',
  manual_override: 'Manual override (documented)',
};

/** Eligibility shape returned by getReengagementEligibility (subset the UI needs). */
export interface ReengagementEligibility {
  loanId: string;
  /** Client Care client resolved from the loan's customer; null if no roster match. */
  clientId: string | null;
  canLogActivity: boolean;
  canSendEmail: boolean;
  eligible: boolean;
  /** Attempt-gate portion only (attempts/window/distinct days); drives the
   *  'Uncontactable' loss-reason gate, independent of email/cooldown/suppression. */
  contactGatePassed: boolean;
  attemptCount: number;
  distinctDayCount: number;
  windowSpanDays: number;
  minAttempts: number;
  minWindowDays: number;
  requireDistinctDays: boolean;
  reasons: string[];
  hasEmailOnFile: boolean;
  clientEmail: string | null;
  emailSource: 'client_care' | 'broker_entered' | null;
  alreadySuppressed: boolean;
  lastSentAt: string | null;
  inCooldown: boolean;
}
